import { Location } from "@/reducers/locationsSlice"
import i18n from "@/lib/i18n/i18n"
import { DAY_IN_MS } from "./plannerGeometry"
import { ProximityPoint, VehicleSuggestion } from "./vehicleSuggestions"

// Dojazd drogowy policzony przez gps (HERE Matrix). Osobny typ od
// VehicleSuggestion, bo dochodzi asynchronicznie i tylko dla topki — sugestia
// jest kompletna bez niego.
export type RoadDistance = { km: number; durationMinutes?: number }

// Location z AutocompleteGooglePlaces trzyma współrzędne jako stringi, a przy
// wklejonych koordynatach formattedAddress jest surowym stringiem "lat, lon" —
// komponent NIE geokoduje odwrotnie, więc nie ma tu miejscowości do wzięcia.
export const parsePlanningPoint = (
  location: Location,
): ProximityPoint | null => {
  const lat = Number(location.lat)
  const lon = Number(location.lon)
  if (!Number.isFinite(lat) || !Number.isFinite(lon)) return null
  // (0, 0) to nie punkt na Atlantyku, to nieustawiona lokalizacja.
  if (lat === 0 && lon === 0) return null
  if (lat < -90 || lat > 90 || lon < -180 || lon > 180) return null
  return {
    lat,
    lon,
    label: location.locality ?? location.formattedAddress,
  }
}

// Czy o zajętości w tym dniu w ogóle coś wiemy.
//
// Czas zajęty pochodzi z okna osi czasu (kolumny, które grid faktycznie ma —
// feedy providerów i planowane zlecenia) plus z bieżących tras. Dla dnia poza
// tym oknem KAŻDY pojazd wraca z hasFreeWindow === true, co nie jest wiedzą, a
// brakiem danych — i nie wolno tego pokazać jako "wolny". Granicę bierzemy z
// dateOffsets, czyli z tego, co oś czasu naprawdę objęła, a nie z zaszytego
// -7/+14: te liczby są tylko klamrą i zmieniają się razem z danymi.
export const isDateWithinTimelineWindow = (
  date: Date,
  timelineStart: Date,
  dateOffsets: number[],
): boolean => {
  if (dateOffsets.length === 0) return false
  // Zaokrąglenie, nie obcięcie: zmiana czasu robi z doby 23 lub 25 godzin, więc
  // "jutro" wypada wtedy na 0,96 albo 1,04 dnia od początku osi.
  const offset = Math.round(
    (date.getTime() - timelineStart.getTime()) / DAY_IN_MS,
  )
  return (
    offset >= dateOffsets[0] && offset <= dateOffsets[dateOffsets.length - 1]
  )
}

export const formatDriveTime = (minutes: number): string => {
  const rounded = Math.round(minutes)
  const hours = Math.floor(rounded / 60)
  const rest = rounded % 60
  if (hours === 0) return `${rest} min`
  if (rest === 0) return `${hours} h`
  return `${hours} h ${rest} min`
}

// Podpis pod tablicami w trybie punktu. Miejscowość pozycji odniesienia zostaje
// w podpisie zawsze, gdy ją znamy: dla daty odległej w przyszłość odniesieniem
// jest zwykle live GPS, a dyspozytor musi widzieć, od czego liczymy.
export const approachLabel = (
  suggestion: VehicleSuggestion,
  road?: RoadDistance,
): string => {
  if (road) {
    const parts = [i18n.t("planner.labels.roadKm", { km: Math.round(road.km) })]
    if (road.durationMinutes !== undefined) {
      parts.push(formatDriveTime(road.durationMinutes))
    }
    if (suggestion.referenceLabel) parts.push(suggestion.referenceLabel)
    return parts.join(" · ")
  }
  if (suggestion.distanceKm === undefined)
    return i18n.t("planner.labels.noPosition")
  return [`${Math.round(suggestion.distanceKm)} km`, suggestion.referenceLabel]
    .filter(Boolean)
    .join(" · ")
}
